/**
 * Zod schemas — the validation authority.
 *
 * ONE schema per table, used at every boundary: form input, IPC, a restore
 * file, a CSV import and a database read. A value that has passed a Zod
 * schema cannot disagree with the data model, because they are derived from
 * the same field names.
 *
 * Money is validated as a whole number of paise. A schema that accepted a
 * float here would let `cgst: 17743.7286` back into the system — the exact
 * defect 1.6.x shipped.
 */
import { z } from 'zod';
import { DOC_TYPES } from '../docTypes';
import { DOC_STATUSES } from '../lifecycle';

// ── primitives ───────────────────────────────────────────────────────────

/** A ULID: 26 Crockford base32 characters. */
export const ulidSchema = z
  .string()
  .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Must be a ULID');

/** 'YYYY-MM-DD'. Never a Date — a Date is a UTC instant. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Must be YYYY-MM-DD');

/** Full ISO-8601 timestamp, for audit and creation times. */
export const isoTimestampSchema = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Must be a parseable timestamp');

/** Whole paise. Never a float. */
export const moneySchema = z
  .number()
  .int('Money must be a whole number of paise')
  .safe();

/** Whole milli-units (0.001). */
export const quantitySchema = z.number().int().safe();

/** Whole basis points (0.01%). */
export const basisPointsSchema = z.number().int().min(0).max(10_000);

export const docTypeSchema = z.enum(DOC_TYPES);
export const docStatusSchema = z.enum(DOC_STATUSES);

export const partyKindSchema = z.enum(['CUSTOMER', 'SUPPLIER']);
export const supplyTypeSchema = z.enum(['INTRA', 'INTER', 'EXPORT', 'SEZ']);

/** Two-digit GST state code. */
export const gstStateCodeSchema = z
  .string()
  .regex(/^\d{2}$/, 'Must be a two-digit GST state code');

/** Shape check. GSTIN checksum validation is a separate concern. */
export const gstinSchema = z
  .string()
  .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z\d[A-Z\d]$/, 'Invalid GSTIN')
  .optional()
  .or(z.literal(''));

/** Financial-year label in canonical four-digit form: '2026-27'. */
export const financialYearSchema = z
  .string()
  .regex(/^\d{4}-\d{2}$/, 'Must be the canonical form YYYY-YY, e.g. 2026-27');

// ── shared shapes ────────────────────────────────────────────────────────

export const addressLinesSchema = z.array(z.string().max(200)).max(10);

export const partySnapshotSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(200),
  gstin: z.string().optional(),
  stateCode: gstStateCodeSchema,
  stateName: z.string().min(1),
  addressLines: addressLinesSchema,
  phone: z.string().max(40).optional(),
  email: z.string().email().max(200).optional(),
});

export const chargeRowSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(80),
  type: z.enum(['PERCENT', 'AMOUNT']),
  value: z.number(),
  amountPaise: moneySchema,
  taxable: z.boolean(),
  taxRateBp: basisPointsSchema.optional(),
});

// ── documents ────────────────────────────────────────────────────────────

export const documentLineSchema = z.object({
  id: ulidSchema,
  businessId: z.string().min(1),
  documentId: ulidSchema,
  docType: docTypeSchema,
  lineNo: z.number().int().positive(),

  productId: z.string().optional(),
  stockLocationId: z.string().optional(),
  description: z.string().min(1).max(500),
  descriptionExtra: z.string().max(500).optional(),
  hsnSac: z.string().max(20).optional(),
  unit: z.string().min(1).max(12),
  productSnapshot: z.string().max(200).optional(),

  quantityMilli: quantitySchema,
  freeQuantityMilli: quantitySchema,
  ratePaise: moneySchema,
  discountType: z.enum(['NONE', 'PERCENT', 'AMOUNT']),
  discountValue: z.number().min(0),
  discountPaise: moneySchema,
  allocatedDocDiscountPaise: moneySchema,
  taxablePaise: moneySchema,

  taxRateId: z.string().optional(),
  taxRateBp: basisPointsSchema,
  cgstRateBp: z.number().int().min(0),
  sgstRateBp: z.number().int().min(0),
  igstRateBp: z.number().int().min(0),
  cgstPaise: moneySchema,
  sgstPaise: moneySchema,
  igstPaise: moneySchema,
  cessRateBp: basisPointsSchema,
  cessPaise: moneySchema,
  totalPaise: moneySchema,
  rateWithTaxPaise: moneySchema,

  fulfilledQtyMilli: quantitySchema,
  invoicedQtyMilli: quantitySchema,
  returnedQtyMilli: quantitySchema,

  incomeAccountId: z.string().optional(),
  expenseAccountId: z.string().optional(),
  isActive: z.boolean(),
});

/**
 * The document header.
 *
 * `superRefine` carries the invariants that make an invoice internally
 * consistent — the same ones the calculation engine guarantees. A restored or
 * imported record that violates them is rejected rather than trusted, because
 * a report built on it would not reconcile.
 */
export const documentSchema = z
  .object({
    id: ulidSchema,
    businessId: z.string().min(1),
    docType: docTypeSchema,
    docNumber: z.string().min(1).max(60),
    docNumberSeq: z.number().int().positive(),
    financialYear: financialYearSchema,
    docDate: isoDateSchema,
    dueDate: isoDateSchema.optional(),
    expectedDate: isoDateSchema.optional(),
    expiryDate: isoDateSchema.optional(),

    customerId: z.string().optional(),
    supplierId: z.string().optional(),
    partySnapshot: partySnapshotSchema,

    placeOfSupplyCode: gstStateCodeSchema,
    supplyType: supplyTypeSchema,
    reverseCharge: z.boolean(),

    quotationId: z.string().optional(),
    salesOrderId: z.string().optional(),
    purchaseOrderId: z.string().optional(),
    originalDocId: z.string().optional(),
    sourceKind: z.string().max(40).optional(),
    returnReasonCode: z.string().max(40).optional(),
    linkedChildDocIds: z.array(z.string()),

    grossSubtotalPaise: moneySchema,
    lineDiscountPaise: moneySchema,
    invoiceDiscountPaise: moneySchema,
    totalDiscountPaise: moneySchema,
    taxablePaise: moneySchema,
    taxableChargesPaise: moneySchema,
    nonTaxableChargesPaise: moneySchema,
    cgstPaise: moneySchema,
    sgstPaise: moneySchema,
    igstPaise: moneySchema,
    cessPaise: moneySchema,
    tcsPaise: moneySchema,
    taxTotalPaise: moneySchema,
    preRoundOffPaise: moneySchema,
    roundOffPaise: moneySchema,
    grandTotalPaise: moneySchema,
    totalQtyMilli: quantitySchema,
    totalFreeQtyMilli: quantitySchema,
    amountInWords: z.string().min(1).max(200),

    charges: z.array(chargeRowSchema),
    notes: z.string().max(4000).optional(),
    terms: z.string().max(4000).optional(),
    declaration: z.string().max(1000).optional(),
    coverLetter: z.string().max(4000).optional(),
    coverLetterEnabled: z.boolean().optional(),

    status: docStatusSchema,
    issuedAt: isoTimestampSchema.optional(),
    cancelledAt: isoTimestampSchema.optional(),
    cancelledBy: z.string().optional(),
    cancelReason: z.string().max(500).optional(),
    returnedAt: isoTimestampSchema.optional(),

    allocatedPaise: moneySchema,
    balancePaise: moneySchema,

    calcVersion: z.string().min(1),
    calcSnapshot: z.string(),
    legacyData: z.string().optional(),

    createdAt: isoTimestampSchema,
    createdBy: z.string().min(1),
    updatedAt: isoTimestampSchema,
    updatedBy: z.string().min(1),
  })
  .superRefine((d, ctx) => {
    const at = (field: string, message: string): void => {
      ctx.addIssue({ code: 'custom', path: [field], message });
    };
    const n = (k: keyof typeof d): number => d[k] as number;

    // tax must be a function of the taxable value, or a GST return built on
    // this row will not reconcile with the invoice.
    const taxSum = n('cgstPaise') + n('sgstPaise') + n('igstPaise') + n('cessPaise');
    if (taxSum !== n('taxTotalPaise')) {
      at('taxTotalPaise', 'Tax total must equal CGST + SGST + IGST + cess');
    }
    if (n('grandTotalPaise') !== n('preRoundOffPaise') + n('tcsPaise') + n('roundOffPaise')) {
      at('grandTotalPaise', 'Grand total must equal pre-round-off + TCS + round-off');
    }
    if (n('preRoundOffPaise') !==
      n('taxablePaise') + n('taxTotalPaise') + n('nonTaxableChargesPaise')) {
      at('preRoundOffPaise', 'Pre-round-off must equal taxable + tax + non-taxable charges');
    }
    if (n('taxablePaise') < 0 || n('grandTotalPaise') < 0) {
      at('grandTotalPaise', 'Amounts cannot be negative');
    }

    // exactly one of (CGST+SGST) or IGST, never both and never neither
    const hasSplit = n('cgstPaise') + n('sgstPaise') > 0;
    const hasIgst = n('igstPaise') > 0;
    if (hasSplit === hasIgst) {
      at('igstPaise', 'Either CGST+SGST or IGST must be charged, never both or neither');
    }
    // supply type must match the tax actually charged
    if (d.supplyType === 'INTER' && !hasIgst && n('taxablePaise') > 0) {
      at('supplyType', 'Inter-state supply must be charged IGST');
    }
    if (d.supplyType === 'INTRA' && hasIgst) {
      at('supplyType', 'Intra-state supply must be charged CGST + SGST, not IGST');
    }

    // a sales document must name a customer; a purchase one, a supplier
    const isSales = [
      'QUOTATION', 'SALES_ORDER', 'DELIVERY_NOTE', 'SALES_INVOICE', 'CREDIT_NOTE', 'SALES_RETURN',
    ].includes(d.docType);
    if (isSales && !d.customerId) at('customerId', 'A sales document requires a customer');
    if (!isSales && !d.supplierId) at('supplierId', 'A purchase document requires a supplier');

    // a cancelled document must say why and when
    if (d.status === 'CANCELLED' && !d.cancelledAt) {
      at('cancelledAt', 'A cancelled document requires a cancellation timestamp');
    }
  });
